import React, { useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { editGroup } from '../../actions/roleActions';
import {
  endLoading,
  startLoading,
} from '../../reducers/loading/loading.action';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import StylishNewInput from '../DesignSystems/New/StylishNewInput';
import { toast } from 'react-toastify';
import {
  MAX_SEERIST_API_KEY_CHARS,
  normalizeSeeristApiKey,
} from './normalizeSeeristApiKey';

const emailRegex = /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-z]+)$/;

type EditGroupModalProps = {
  group: any;
  onClose: () => void;
  startDelete: () => void;
  show: boolean;
};
const EditGroupModal: React.FC<EditGroupModalProps> = ({
  group,
  onClose,
  startDelete,
  show,
}) => {
  const dispatch = useDispatch();

  const currentlySelectedGroup = useSelector(
    (s: any) => s.app.currentlySelectedGroup
  );
  const RBACPermissions = useSelector((state: any) => {
    return state.app.validateRBACPermissionForActionResult;
  });

  const {
    resetField,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitted },
  } = useForm({ mode: 'onChange' });

  const storedSeeristKey = currentlySelectedGroup.seerist_api_key ?? '';
  const normalizedStoredSeeristKey = useMemo(
    () => normalizeSeeristApiKey(storedSeeristKey),
    [storedSeeristKey]
  );
  const clearedOversizedSeeristKey =
    normalizedStoredSeeristKey.length > MAX_SEERIST_API_KEY_CHARS;
  const seeristKeyDefault = clearedOversizedSeeristKey
    ? ''
    : normalizedStoredSeeristKey;

  const storedTakSecret =
    currentlySelectedGroup.tak_event_hub_client_secret ?? '';
  const normalizedStoredTakSecret = useMemo(
    () => normalizeSeeristApiKey(storedTakSecret),
    [storedTakSecret]
  );
  const clearedOversizedTakSecret =
    normalizedStoredTakSecret.length > MAX_SEERIST_API_KEY_CHARS;
  const takSecretDefault = clearedOversizedTakSecret
    ? ''
    : normalizedStoredTakSecret;

  const applyNormalizedSecretField = (
    raw: string,
    onChange: (value: string) => void,
    label: string
  ) => {
    const normalized = normalizeSeeristApiKey(raw);
    onChange(normalized);
    if (normalized.length > MAX_SEERIST_API_KEY_CHARS) {
      toast.error(
        `Pasted ${label} looks too long (${normalized.length} chars).`
      );
    }
  };

  const onEditGroupSubmit: SubmitHandler<any> = async ({
    editGroupName,
    default_sender_email,
    arcgis_portal_url,
    oauth_client_id,
    seerist_api_key,
    tak_event_hub_client_id,
    tak_event_hub_client_secret,
    perplexity_web_search,
    parallel_web_research,
  }) => {
    const normalizedSeeristKey = normalizeSeeristApiKey(seerist_api_key);
    if (normalizedSeeristKey.length > MAX_SEERIST_API_KEY_CHARS) {
      toast.error(
        `Seerist API key is too long (${normalizedSeeristKey.length} chars). Paste only the raw token — not the email, Cookie header, or "Bearer " prefix.`
      );
      return;
    }

    const normalizedTakClientId = normalizeSeeristApiKey(
      tak_event_hub_client_id
    );
    const normalizedTakClientSecret = normalizeSeeristApiKey(
      tak_event_hub_client_secret
    );
    if (
      normalizedTakClientId.length > MAX_SEERIST_API_KEY_CHARS ||
      normalizedTakClientSecret.length > MAX_SEERIST_API_KEY_CHARS
    ) {
      toast.error(
        `TAK Event Hub credential is too long (max ${MAX_SEERIST_API_KEY_CHARS} chars).`
      );
      return;
    }

    dispatch(startLoading());
    const existingGroup = { ...group };
    existingGroup.key = editGroupName;
    existingGroup.group_guid = group.value;
    existingGroup.group_name = editGroupName;
    existingGroup.daily_digest_team = group.dailyDigestTeam;
    existingGroup.default_sender_email = default_sender_email;
    existingGroup.arcgis_portal_url = arcgis_portal_url;
    existingGroup.oauth_client_id = oauth_client_id;
    existingGroup.seerist_api_key = normalizedSeeristKey;
    existingGroup.tak_event_hub_client_id = normalizedTakClientId;
    existingGroup.tak_event_hub_client_secret = normalizedTakClientSecret;
    existingGroup.perplexity_web_search = !!perplexity_web_search;
    existingGroup.parallel_web_research = !!parallel_web_research;

    await dispatch(editGroup(existingGroup));
    dispatch(endLoading());
    onClose();
  };

  const onCloseRequest = () => {
    resetField('editGroupName');
    resetField('default_sender_email');
    resetField('arcgis_portal_url');
    resetField('oauth_client_id');
    resetField('seerist_api_key');
    resetField('tak_event_hub_client_id');
    resetField('tak_event_hub_client_secret');
    resetField('perplexity_web_search');
    resetField('parallel_web_research');
    onClose();
  };

  return (
    <Modal show={show} onHide={onCloseRequest} centered>
      <form onSubmit={handleSubmit(onEditGroupSubmit)}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Edit Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Name</label>
            {/* @ts-ignore */}
            <Controller
              control={control}
              name="editGroupName"
              rules={{ required: true }}
              defaultValue={currentlySelectedGroup.group_name}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e: any) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
            {errors.editGroupName?.type === 'required' && (
              <span className="form-text form-error">
                This field is required
              </span>
            )}
            {errors.editGroupName?.type === 'custom' && (
              <span className="form-text form-error">
                This Group name is in use. Please enter a new one.
              </span>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Organization From Email</label>
            <Controller
              control={control}
              name="default_sender_email"
              rules={{
                validate: (v) =>
                  !v || emailRegex.test(v) || 'Invalid email address',
              }}
              defaultValue={currentlySelectedGroup.default_sender_email}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e: any) => onChange(e.target.value)}
                  value={value}
                />
              )}
            />
            {errors.default_sender_email?.type === 'validate' && (
              <span className="form-text form-error">
                Please Enter a Valid Email
              </span>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">ArcGIS Portal URL</label>
            <Controller
              control={control}
              name="arcgis_portal_url"
              rules={{ required: false }}
              defaultValue={currentlySelectedGroup.arcgis_portal_url ?? ''}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e) => onChange(e.target.value)}
                  value={value ?? ''}
                />
              )}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">OAuth Client ID</label>
            <Controller
              control={control}
              name="oauth_client_id"
              rules={{ required: false }}
              defaultValue={currentlySelectedGroup.oauth_client_id ?? ''}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  onChange={(e) => onChange(e.target.value)}
                  value={value ?? ''}
                />
              )}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Seerist API Key</label>
            <Controller
              control={control}
              name="seerist_api_key"
              rules={{ required: false }}
              defaultValue={seeristKeyDefault}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="password"
                  autoComplete="off"
                  onChange={(e) =>
                    applyNormalizedSecretField(
                      e.target.value,
                      onChange,
                      'Seerist key'
                    )
                  }
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                    const pasted = e.clipboardData?.getData('text') ?? '';
                    if (!pasted) return;
                    e.preventDefault();
                    applyNormalizedSecretField(pasted, onChange, 'Seerist key');
                  }}
                  value={value ?? ''}
                  placeholder="Raw Seerist bearer token (no Bearer prefix)"
                />
              )}
            />
            <span className="form-text text-muted">
              Paste only the raw bearer token from Seerist — not the email, not
              a Cookie header, and without the &quot;Bearer &quot; prefix. Used
              by Pratus AI and automated report sections.
            </span>
            {clearedOversizedSeeristKey && (
              <span className="form-text form-error d-block">
                A previously saved key was too large to send and was cleared.
                Re-paste the raw Seerist token.
              </span>
            )}
          </div>
          <div className="mb-3">
            <Controller
              control={control}
              name="perplexity_web_search"
              defaultValue={!!currentlySelectedGroup.perplexity_web_search}
              render={({ field: { onChange, value } }) => (
                <div className="form-check">
                  <input
                    id="perplexity_web_search"
                    type="checkbox"
                    className="form-check-input"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="perplexity_web_search"
                  >
                    Use Perplexity Sonar Pro for web search
                  </label>
                </div>
              )}
            />
            <span className="form-text text-muted">
              Web searches in PRATUS AI chat and AI report sections use
              Perplexity Sonar Pro, with cited sources. Falls back to standard
              web search if Perplexity is unavailable.
            </span>
          </div>
          <div className="mb-3">
            <Controller
              control={control}
              name="parallel_web_research"
              defaultValue={!!currentlySelectedGroup.parallel_web_research}
              render={({ field: { onChange, value } }) => (
                <div className="form-check">
                  <input
                    id="parallel_web_research"
                    type="checkbox"
                    className="form-check-input"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="parallel_web_research"
                  >
                    Use Parallel for web research
                  </label>
                </div>
              )}
            />
            <span className="form-text text-muted">
              When drafting ICS forms, PRATUS AI gathers web evidence with
              Parallel so each drafted box can cite a source. Chat and reports
              use it when Perplexity is off or unavailable.
            </span>
          </div>
          <div className="mb-3">
            <label className="form-label">TAK Event Hub Client ID</label>
            <Controller
              control={control}
              name="tak_event_hub_client_id"
              rules={{ required: false }}
              defaultValue={
                currentlySelectedGroup.tak_event_hub_client_id ?? ''
              }
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="text"
                  autoComplete="off"
                  onChange={(e) =>
                    applyNormalizedSecretField(
                      e.target.value,
                      onChange,
                      'TAK Event Hub Client ID'
                    )
                  }
                  value={value ?? ''}
                  placeholder="Azure Entra application (client) ID"
                />
              )}
            />
            <span className="form-text text-muted">
              App registration client ID used by COMS to consume the TAK CoT
              Event Hub feed for this organization.
            </span>
          </div>
          <div className="mb-3">
            <label className="form-label">TAK Event Hub Client Secret</label>
            <Controller
              control={control}
              name="tak_event_hub_client_secret"
              rules={{ required: false }}
              defaultValue={takSecretDefault}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  type="password"
                  autoComplete="off"
                  onChange={(e) =>
                    applyNormalizedSecretField(
                      e.target.value,
                      onChange,
                      'TAK Event Hub Client Secret'
                    )
                  }
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                    const pasted = e.clipboardData?.getData('text') ?? '';
                    if (!pasted) return;
                    e.preventDefault();
                    applyNormalizedSecretField(
                      pasted,
                      onChange,
                      'TAK Event Hub Client Secret'
                    );
                  }}
                  value={value ?? ''}
                  placeholder="Azure Entra client secret"
                />
              )}
            />
            <span className="form-text text-muted">
              Client secret for the TAK Event Hub app registration. Stored like
              other organization API credentials.
            </span>
            {clearedOversizedTakSecret && (
              <span className="form-text form-error d-block">
                A previously saved secret was too large to send and was cleared.
                Re-paste the client secret.
              </span>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              type="button"
              className="button--error"
              onClick={startDelete}
              disabled={!RBACPermissions['Open or Close Group']}
            >
              Delete this Organization
            </StylishNewButton>
            <StylishNewButton
              type="button"
              className="button--secondary"
              onClick={onCloseRequest}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              type="submit"
              className="button--primary"
              disabled={isSubmitted && !isValid}
            >
              Save Organization
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditGroupModal;
